/**
 * Centered moving-average smoothing of a daily series. `windowDays` is the full
 * window width; each point becomes the mean of the window centered on it,
 * shrinking the window at the edges (so length is preserved and there are no
 * NaN ends). A visual-only transform — it does not affect the forecast.
 */
export function smooth(values: number[], windowDays: number): number[] {
  if (windowDays <= 1) return values.slice();
  const half = Math.floor(windowDays / 2);
  return values.map((_, i) => {
    const lo = Math.max(0, i - half);
    const hi = Math.min(values.length - 1, i + half);
    let sum = 0;
    for (let j = lo; j <= hi; j++) sum += values[j];
    return sum / (hi - lo + 1);
  });
}

/**
 * Centered Gaussian-weighted moving average. For each index the window is
 * [i-half, i+half] clamped to the array bounds; values are weighted by a
 * Gaussian whose σ makes the window span ±2.5σ, so the weights taper to ≈0 at
 * the edges. Because points fade in/out with near-zero weight as the window
 * slides, the result has no endpoint ripple (unlike Hamming, whose edge weight
 * is 0.08). The result is the weighted mean over the in-bounds part.
 * windowDays <= 1 returns a copy. Length is preserved; no NaN at the edges.
 *
 * The window index `k` runs 0..W-1 across the *full* (unclamped) window, so the
 * centre keeps the heaviest weight even where the window is clipped at an edge.
 */
export function gaussianSmooth(values: number[], windowDays: number): number[] {
  if (windowDays <= 1) return values.slice();
  const width = Math.floor(windowDays);
  if (width <= 1) return values.slice();
  const half = Math.floor(width / 2);
  // σ sized so the half-window equals 2.5σ → edge weight ≈ exp(-3.125) ≈ 0.044.
  const sigma = (width - 1) / 5;
  const weights: number[] = [];
  for (let k = 0; k < width; k++) {
    const x = (k - (width - 1) / 2) / sigma;
    weights.push(Math.exp(-0.5 * x * x));
  }
  return values.map((_, i) => {
    let weighted = 0;
    let weightSum = 0;
    // Offset k spans the full window [i-half, i-half+width); clamp to bounds.
    for (let k = 0; k < width; k++) {
      const j = i - half + k;
      if (j < 0 || j >= values.length) continue;
      weighted += weights[k] * values[j];
      weightSum += weights[k];
    }
    return weightSum === 0 ? values[i] : weighted / weightSum;
  });
}
